/** Same contract in web and pinto; parity is checked by scripts/test-conversion-contract.mjs. */
export const CONVERSION_STEPS = ["intent", "onboarding_zip", "checkout_return", "billing_check", "billing_blocked", "plan", "zipcode", "store_preview", "walmart_stores", "home_depot_stores"] as const;
export type ConversionStep = typeof CONVERSION_STEPS[number];
const EVENTS = [
  "plan_selected", "offer_presented", "offers_available", "offers_unavailable", "billing_check_failed", "billing_existing_subscription",
  "back_clicked", "close_clicked", "logout_clicked", "continue_clicked", "step_saved", "invalid_zip", "store_selection_missing",
  "save_failed", "geocode_unavailable", "stores_loaded", "stores_empty", "stores_load_failed", "stores_retry_clicked", "offers_retry_clicked",
  "checkout_requested", "checkout_redirected", "checkout_link_missing", "checkout_failed", "plan_unavailable",
  "purchase_requested", "purchase_cancelled", "purchase_failed", "purchase_client_confirmed", "downgrade_deferred", "entitlement_pending", "entitlement_available",
  "restore_requested", "restore_empty", "restore_failed", "restore_entitlement_available", "restore_entitlement_pending",
  "intent_online_selected", "intent_local_selected", "intent_saved",
  "store_preview_shown", "store_preview_continue", "store_preview_browse_online", "free_link_clicked", "free_skip_kept",
] as const;
export type ConversionEvent = typeof EVENTS[number];
type Props = Record<string, unknown>;
type Sender = (name: string, properties?: Props) => void;
const enumValues: Record<string, readonly string[]> = {
  step: CONVERSION_STEPS, plan_id: ["basic", "plus", "pro"], platform: ["web", "ios", "android"],
  mode: ["setup", "upgrade", "onboarding"], retailer: ["walmart", "home_depot"],
  error_kind: ["network", "client", "server", "unknown"],
  preview_mode: ["own", "nearby", "leads"],
};
const numericKeys = new Set(["price", "intro_price", "result_count", "selected_count", "http_status", "provider_code", "preview_stores", "preview_deals"]);

/** Never accept raw errors, URLs, ZIPs, store IDs, search text, or customer details. */
export function sanitizeConversionProperties(props: Props): Props {
  const safe: Props = {};
  for (const [key, value] of Object.entries(props)) {
    if (typeof value === "string" && enumValues[key]?.includes(value)) safe[key] = value;
    else if (numericKeys.has(key) && typeof value === "number" && Number.isFinite(value) && value >= 0) safe[key] = value;
    else if (key === "offer_available" && typeof value === "boolean") safe[key] = value;
    else if (key === "currency" && typeof value === "string" && /^[A-Z]{3}$/.test(value)) safe[key] = value;
  }
  return safe;
}

export function conversionErrorProperties(error: unknown): Props {
  const value = error && typeof error === "object" ? error as { status?: unknown; response?: { status?: unknown }; code?: unknown } : {};
  const status = Number(value.response?.status ?? value.status);
  const providerCode = typeof value.code === "number" ? value.code : /^\d{1,3}$/.test(String(value.code)) ? Number(value.code) : undefined;
  return {
    error_kind: status >= 500 ? "server" : status >= 400 ? "client" : value.code === "ERR_NETWORK" ? "network" : "unknown",
    ...(status >= 100 && status <= 599 ? { http_status: status } : {}),
    ...(providerCode !== undefined ? { provider_code: providerCode } : {}),
  };
}

export function createConversionJourney(send: Sender, context: Props) {
  const startedAt = Date.now();
  const flowId = `cf_${startedAt.toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  let step: ConversionStep | null = null;
  let stepStartedAt = startedAt;
  let sequence = 0;
  const seen = new Set<string>();
  let currentContext = sanitizeConversionProperties(context);

  function emit(suffix: string, properties: Props = {}) {
    const now = Date.now();
    try {
      send(`conversion_${suffix}`, {
        ...currentContext, ...(step ? { step } : {}), ...sanitizeConversionProperties(properties),
        schema_version: 1, flow_id: flowId, event_id: `${flowId}_${++sequence}`,
        occurred_at: new Date(now).toISOString(), elapsed_ms: now - startedAt, step_ms: now - stepStartedAt,
      });
    } catch { /* Telemetry must not interrupt the product flow. */ }
  }

  return {
    setContext(properties: Props) { currentContext = { ...currentContext, ...sanitizeConversionProperties(properties) }; },
    view(next: ConversionStep | null) {
      if (!next || next === step || !CONVERSION_STEPS.includes(next)) return;
      step = next;
      stepStartedAt = Date.now();
      emit(`${next}_viewed`);
    },
    event(name: ConversionEvent, properties: Props = {}) {
      if (!(EVENTS as readonly string[]).includes(name)) return;
      emit(name, properties);
    },
    once(key: string, name: ConversionEvent, properties: Props = {}) {
      if (seen.has(key)) return;
      seen.add(key);
      this.event(name, properties);
    },
  };
}
